import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Search } from "lucide-react";
import { vaccinationApi, vaccinationQueryKeys } from "../../shared/api/vaccination";
import { patientsApi } from "../../shared/api/patients";
import type { PatientSearchResult } from "../../shared/types/patient";
import type { StockRow } from "../../shared/api/inventory";
import { LotCombobox } from "./LotCombobox";
import { formatDate } from "../../shared/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * Tiêm chủng · Sổ tiêm &amp; Tra cứu thu hồi lô (P0A). Two tools: a patient's immunization history,
 * and a batch-recall lookup (which patients received a given lot) — the traceability that lets a
 * clinic answer a vaccine withdrawal.
 */
export function VaccinationRecordsPage() {
  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Sổ tiêm &amp; Tra cứu thu hồi lô</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Xem lịch sử tiêm của một bệnh nhân, hoặc truy vết mọi bệnh nhân đã dùng một lô.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PatientHistory />
        <BatchRecall />
      </div>
    </div>
  );
}

const birthYear = (dob?: string | null) => (dob ? dob.slice(0, 4) : null);

function PatientHistory() {
  const [q, setQ] = useState("");
  // #18654: keep the whole selected patient (not just the id) so we can show a banner of who is
  // being looked up.
  const [patient, setPatient] = useState<PatientSearchResult | null>(null);
  const search = useQuery({
    queryKey: ["patients", "search", q],
    queryFn: () => patientsApi.search(q, { page: 0, size: 8 }),
    enabled: q.trim().length >= 2,
  });
  const history = useQuery({
    queryKey: vaccinationQueryKeys.history(patient?.id ?? "none"),
    queryFn: () => vaccinationApi.history(patient!.id),
    enabled: !!patient,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Sổ tiêm theo bệnh nhân</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* One consistent way to pick/change a patient (#18654): a search box while none is
            chosen; once chosen, a patient banner + a single "Đổi bệnh nhân" action (mirrors the
            batch picker on the right). */}
        {!patient ? (
          <>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Tìm bệnh nhân theo tên / SĐT / mã BN…"
                className="pl-9"
                aria-label="Tìm bệnh nhân"
              />
            </div>
            {q.trim().length >= 2 ? (
              <ul className="divide-y divide-border rounded-md border border-border">
                {search.data?.content.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                      onClick={() => {
                        setPatient(p);
                        setQ("");
                      }}
                    >
                      {/* Tên · Năm sinh, rồi Mã BN · SĐT — để phân biệt bệnh nhân trùng tên. */}
                      <div>
                        <span className="font-medium text-foreground">{p.fullName}</span>
                        {birthYear(p.dateOfBirth) && (
                          <span className="text-muted-foreground">
                            {" "}
                            · {birthYear(p.dateOfBirth)}
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {p.patientCode ?? "—"}
                        {p.phoneE164 ? ` · ${p.phoneE164}` : ""}
                      </div>
                    </button>
                  </li>
                ))}
                {(search.data?.content.length ?? 0) === 0 && !search.isFetching && (
                  <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                    Không tìm thấy bệnh nhân phù hợp.
                  </li>
                )}
              </ul>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Tìm và chọn bệnh nhân để xem sổ tiêm.
              </p>
            )}
          </>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
              <div className="text-sm">
                <div>
                  <span className="font-semibold uppercase text-foreground">
                    {patient.fullName}
                  </span>
                  {birthYear(patient.dateOfBirth) && (
                    <span className="text-muted-foreground">
                      {" "}
                      · {birthYear(patient.dateOfBirth)}
                    </span>
                  )}
                </div>
                <div className="font-mono text-xs text-muted-foreground">
                  {patient.patientCode ?? "—"}
                  {patient.phoneE164 ? ` · ${patient.phoneE164}` : ""}
                </div>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPatient(null)}
              >
                <ArrowLeft />
                Đổi bệnh nhân
              </Button>
            </div>
            <div className="rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vaccine</TableHead>
                    <TableHead>Mũi</TableHead>
                    <TableHead>Ngày tiêm</TableHead>
                    <TableHead>Lô</TableHead>
                    <TableHead>Nguồn</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.data?.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium text-foreground">
                        {r.vaccineName ?? "—"}
                      </TableCell>
                      <TableCell>{r.doseNo}</TableCell>
                      <TableCell>{formatDate(r.administeredAt)}</TableCell>
                      <TableCell>{r.lotNumber ?? "—"}</TableCell>
                      <TableCell>
                        {r.source === "EXTERNAL" ? "Ngoại viện" : "Nội viện"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(history.data?.length ?? 0) === 0 && !history.isFetching && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-8 text-center text-muted-foreground"
                      >
                        Bệnh nhân này chưa có lịch sử tiêm chủng.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function BatchRecall() {
  // #18604 (re-opened): the lot is now picked through a paginated combobox, so this screen no
  // longer loads the clinic's whole stock list just to fill a picker. Holding the selected lot
  // (not just its id) is what lets the closed field show the name without a lookup.
  const [lot, setLot] = useState<StockRow | null>(null);
  const batchId = lot?.batchId ?? "";
  const recall = useQuery({
    queryKey: ["vaccination", "recall", batchId],
    queryFn: () => vaccinationApi.recall(batchId),
    enabled: !!batchId,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Tra cứu thu hồi lô</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="recall-batch">Chọn lô</Label>
          <LotCombobox value={lot} onChange={setLot} />
        </div>
        {batchId && (
          <div className="rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bệnh nhân</TableHead>
                  <TableHead>Mũi</TableHead>
                  <TableHead>Ngày tiêm</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recall.data?.map((r) => (
                  <TableRow key={r.recordId}>
                    {/* #18604: show the patient's name (+ code), not the raw UUID. */}
                    <TableCell>
                      <span className="font-medium text-foreground">
                        {r.patientName ?? "—"}
                      </span>
                      {r.patientCode && (
                        <span className="block font-mono text-xs text-muted-foreground">
                          {r.patientCode}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{r.doseNo}</TableCell>
                    <TableCell>{formatDate(r.administeredAt)}</TableCell>
                  </TableRow>
                ))}
                {(recall.data?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="py-8 text-center text-muted-foreground"
                    >
                      Không có bệnh nhân nào dùng lô này.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
